/* Localized product-description images
   ------------------------------------------------------------------
   Swaps an <img> in the product description for a language-specific
   variant when one actually exists, e.g.

       8.jpg   ->   8-es.jpg     (on the Spanish storefront)

   Lightweight by design:
   - runs during browser idle time, so it never blocks rendering / LCP
   - description images sit below the fold, so the original load is cheap
   - a HEAD request is a header-only existence check (no image body)
   - the localized file is only ever downloaded when it exists
*/
(function () {
  "use strict";

  // Default language per storefront TLD — the locale served when the URL has
  // NO /xx/ path prefix. Shopify prefixes every non-default locale, e.g.
  // shopsta.de/en/products/... is English while shopsta.de/products/... is
  // German. Keyed on the country TLD so it matches with or without "www."
  // Keep these codes in sync with the uploaded image suffixes.
  var tldDefaultLang = {
    fr: "fr",
    nl: "nl",
    be: "nl", // French served under /fr/ (handled by prefix)
    de: "de",
    it: "it",
    es: "es",
    pl: "pl",
    pt: "pt",
    se: "sv",
    dk: "da",
    no: "no",
    hu: "hu",
    ro: "ro",
    cz: "cs",
    at: "de",
    ch: "de",
    lt: "lt",
    lv: "lv",
    ee: "et",
    gr: "el",
    ie: "en", // English default
    co: "en"  // English default; /hr/ /de/ handled by prefix
  };

  // Locales we recognise as a URL path prefix (Shopify ISO codes, which also
  // match our image suffixes). "en" is included so an English path short-
  // circuits the swap and the original images are shown.
  var KNOWN_LANGS = {
    en: 1, fr: 1, nl: 1, de: 1, it: 1, es: 1, pl: 1, pt: 1, sv: 1, da: 1,
    hu: 1, ro: 1, cs: 1, lt: 1, lv: 1, et: 1, el: 1, hr: 1,
    fi: 1, no: 1, sk: 1, sl: 1, tr: 1
  };

  // Country TLD (last hostname label) — matches with or without "www."
  // so shopsta.nl and www.shopsta.nl both resolve to "nl".
  var hostLabels = window.location.hostname.split(".");
  var tld = hostLabels[hostLabels.length - 1].toLowerCase();

  // First path segment: a locale prefix (/en/, /fr/ …) or a route (/products).
  var firstSeg = (window.location.pathname.split("/")[1] || "")
    .toLowerCase()
    .split("-")[0]; // normalise e.g. en-gb -> en

  // An explicit locale in the path always wins; otherwise the TLD default.
  var lang = KNOWN_LANGS[firstSeg] ? firstSeg : (tldDefaultLang[tld] || "en");

  // English (or unmapped) — show the original images, swap nothing.
  if (lang === "en") return;

  var suffix = "-" + lang;

  // Only localise images hosted by Shopify. Supplier/CDN images must remain
  // untouched and must not receive speculative localisation requests.
  function isShopifyHosted(url) {
    try {
      var parsed = new URL(url, window.location.href);
      return parsed.hostname.toLowerCase() === "cdn.shopify.com" ||
        /^\/cdn\/shop\/files\//i.test(parsed.pathname);
    } catch (error) {
      return false;
    }
  }

  // The localized file may have been re-saved in a different format
  // (e.g. original 8.png, uploaded 8-es.jpg). Try the original extension
  // first, then fall back to the other common image formats.
  var EXT_FALLBACKS = ["jpg", "jpeg", "png", "webp"];

  // One page-wide cache is shared by description and gallery images. It keeps
  // both completed results and in-flight HEAD requests, so the same Shopify
  // file is never checked more than once during a page visit.
  var candidateChecks = Object.create(null);
  var lookupResults = Object.create(null);
  var pendingLookups = Object.create(null);

  function candidateKey(url) {
    try {
      var parsed = new URL(url, window.location.href);
      parsed.search = "";
      parsed.hash = "";
      return parsed.href;
    } catch (error) {
      return url.split("?")[0].split("#")[0];
    }
  }

  function candidateExists(url) {
    var key = candidateKey(url);
    if (!candidateChecks[key]) {
      candidateChecks[key] = fetch(url, { method: "HEAD" })
        .then(function (res) { return !!(res && res.ok); })
        .catch(function () { return false; });
    }
    return candidateChecks[key];
  }

  // Resolve the first existing candidate sequentially. The cache stores the
  // winning candidate index (or -1 for no match), allowing each caller to keep
  // its own query string while still sharing the existence result.
  function resolveFirstCandidate(candidates, apply) {
    var key = candidates.map(candidateKey).join("|");

    if (Object.prototype.hasOwnProperty.call(lookupResults, key)) {
      var cachedIndex = lookupResults[key];
      apply(cachedIndex === -1 ? null : candidates[cachedIndex]);
      return;
    }

    if (pendingLookups[key]) {
      pendingLookups[key].push({ candidates: candidates, apply: apply });
      return;
    }

    pendingLookups[key] = [{ candidates: candidates, apply: apply }];

    function finish(index) {
      lookupResults[key] = index;
      var waiting = pendingLookups[key];
      delete pendingLookups[key];
      waiting.forEach(function (item) {
        item.apply(index === -1 ? null : item.candidates[index]);
      });
    }

    function tryCandidate(index) {
      if (index >= candidates.length) {
        finish(-1);
        return;
      }
      candidateExists(candidates[index]).then(function (exists) {
        if (exists) finish(index);
        else tryCandidate(index + 1);
      });
    }

    tryCandidate(0);
  }

  function localize() {
    var imgs = document.querySelectorAll(".description-content img");
    if (!imgs.length) return;

    Array.prototype.forEach.call(imgs, function (img) {
      var src = img.getAttribute("src");
      if (!src || /^(data|blob):/i.test(src)) return;
      if (!isShopifyHosted(src)) return;

      // Split off any query string (?v=…&width=…) before touching the path.
      var q = src.indexOf("?");
      var pathPart = q === -1 ? src : src.slice(0, q);
      var query = q === -1 ? "" : src.slice(q);

      var dot = pathPart.lastIndexOf(".");
      if (dot === -1) return; // no extension — skip

      var base = pathPart.slice(0, dot);
      var origExt = pathPart.slice(dot + 1).toLowerCase();

      // Already pointing at the localized file? leave it alone.
      if (base.slice(-suffix.length) === suffix) return;

      // Source images may carry an explicit English "-en" marker
      // (e.g. xdobo-x-series-1-en.png). Replace it; files with no marker
      // (e.g. xdobo-p2.jpg) just get the suffix appended.
      var localizedBase = base.slice(-3).toLowerCase() === "-en"
        ? base.slice(0, -3) + suffix
        : base + suffix;

      // Build the extension list: original first, then the fallbacks.
      var exts = [origExt];
      for (var j = 0; j < EXT_FALLBACKS.length; j++) {
        if (EXT_FALLBACKS[j] !== origExt) exts.push(EXT_FALLBACKS[j]);
      }

      var candidates = exts.map(function (e) {
        return localizedBase + "." + e + query;
      });

      resolveFirstCandidate(candidates, function (localizedSrc) {
        if (!localizedSrc) return;

        // Lock the original's dimensions before swapping, so a
        // higher-resolution localized file renders at the same size.
        var w = img.naturalWidth;
        var h = img.naturalHeight;
        if (w && h && !img.hasAttribute("width")) {
          img.setAttribute("width", w);
          img.setAttribute("height", h);
          img.style.height = "auto"; // preserve aspect ratio when responsive
        }
        img.removeAttribute("srcset"); // ensure the new src wins
        img.src = localizedSrc;
      });
    });
  }

  var GALLERY_EXTS = ["jpg", "jpeg", "png", "webp"];

  function parseGalleryUrl(url) {
    if (!url || /^(data|blob):/i.test(url)) return null;
    if (!isShopifyHosted(url)) return null;
    var queryAt = url.indexOf("?");
    var hashAt = url.indexOf("#");
    var suffixAt = queryAt === -1 ? hashAt : (hashAt === -1 ? queryAt : Math.min(queryAt, hashAt));
    var path = suffixAt === -1 ? url : url.slice(0, suffixAt);
    var trailing = suffixAt === -1 ? "" : url.slice(suffixAt);
    // Legacy img_url thumbnails append both a size and optional crop operation,
    // e.g. product_75x75_crop_center.jpg. Keep that complete transform after the
    // locale suffix so product-nl_75x75_crop_center.jpg is requested.
    var m = path.match(/^(.*?)(_(?:\d+x\d*|x\d+)(?:_crop_(?:top|center|bottom|left|right))?)?\.(jpe?g|png|webp|gif)$/i);
    if (!m) return null;
    var base = m[1];
    if (base.slice(-suffix.length).toLowerCase() === suffix) return null;
    if (base.slice(-3).toLowerCase() === "-en") base = base.slice(0, -3);
    return { base: base, size: m[2] || "", ext: m[3].toLowerCase(), trailing: trailing };
  }

  function localizedFor(base, size, ext, trailing) {
    return base + suffix + size + "." + ext + (trailing || "");
  }

  function requestGalleryImage(base, origExt, apply) {
    var exts = [origExt];
    GALLERY_EXTS.forEach(function (e) { if (e !== origExt) exts.push(e); });
    var candidates = exts.map(function (ext) {
      return base + suffix + "." + ext;
    });

    resolveFirstCandidate(candidates, function (localizedSrc) {
      if (!localizedSrc) {
        apply(null);
        return;
      }
      var cleanSrc = localizedSrc.split("?")[0].split("#")[0];
      apply(cleanSrc.slice(cleanSrc.lastIndexOf(".") + 1).toLowerCase());
    });
  }

  function swapGalleryImg(img, complete) {
    complete = complete || function () {};
    if (!img) {
      complete();
      return;
    }
    var sourceAttribute = img.hasAttribute("data-gallery-src") ? "data-gallery-src" : "src";
    var p = parseGalleryUrl(img.getAttribute(sourceAttribute));
    if (!p) {
      complete();
      return;
    }
    requestGalleryImage(p.base, p.ext, function (ext) {
      if (ext) {
        img.setAttribute(sourceAttribute, localizedFor(p.base, p.size, ext, p.trailing));
      }
      var srcsetAttribute = img.hasAttribute("data-gallery-srcset") ? "data-gallery-srcset" : "srcset";
      var ss = img.getAttribute(srcsetAttribute);
      if (ext && ss && ss.indexOf("data:") === -1) {
        img.setAttribute(srcsetAttribute, ss.replace(/[^\s,]+/g, function (tok) {
          if (/^[\d.]+x$/.test(tok) || /^[\d]+w$/.test(tok)) return tok;
          var pp = parseGalleryUrl(tok);
          return pp ? localizedFor(pp.base, pp.size, ext, pp.trailing) : tok;
        }));
      }
      complete();
    });
  }

  function swapGalleryZoom(fig, complete) {
    complete = complete || function () {};
    var p = parseGalleryUrl(fig && fig.getAttribute("data-zoom"));
    if (!p) {
      complete();
      return;
    }
    requestGalleryImage(p.base, p.ext, function (ext) {
      if (ext) fig.setAttribute("data-zoom", localizedFor(p.base, p.size, ext, p.trailing));
      complete();
    });
  }

  function swapGalleryBackground(el, complete) {
    complete = complete || function () {};
    if (!el) {
      complete();
      return;
    }
    var deferredBackground = el.getAttribute("data-gallery-background");
    if (deferredBackground) {
      var deferred = parseGalleryUrl(deferredBackground);
      if (!deferred) {
        complete();
        return;
      }
      requestGalleryImage(deferred.base, deferred.ext, function (ext) {
        if (ext) el.setAttribute("data-gallery-background", localizedFor(deferred.base, deferred.size, ext, deferred.trailing));
        complete();
      });
      return;
    }
    var style = el.getAttribute("style") || "";
    var m = style.match(/url\(['"]?([^'")]+)['"]?\)/);
    if (!m) {
      complete();
      return;
    }
    var p = parseGalleryUrl(m[1]);
    if (!p) {
      complete();
      return;
    }
    requestGalleryImage(p.base, p.ext, function (ext) {
      if (ext) el.setAttribute("style", style.replace(m[1], localizedFor(p.base, p.size, ext, p.trailing)));
      complete();
    });
  }

  function localizeGalleryFigure(figure, complete) {
    complete = complete || function () {};
    if (!figure || figure.getAttribute("data-gallery-index") === "0") {
      complete();
      return;
    }

    if (figure.__shopstaLocalizationState === "complete") {
      complete();
      return;
    }

    if (figure.__shopstaLocalizationState === "pending") {
      figure.__shopstaLocalizationCallbacks.push(complete);
      return;
    }

    figure.__shopstaLocalizationState = "pending";
    figure.__shopstaLocalizationCallbacks = [complete];
    var remaining = 3;

    function taskComplete() {
      remaining -= 1;
      if (remaining) return;
      figure.__shopstaLocalizationState = "complete";
      var callbacks = figure.__shopstaLocalizationCallbacks;
      figure.__shopstaLocalizationCallbacks = [];
      callbacks.forEach(function (callback) { callback(); });
    }

    swapGalleryImg(figure.querySelector("[data-gallery-image]"), taskComplete);
    swapGalleryZoom(figure, taskComplete);
    swapGalleryBackground(figure.querySelector(".product-gallery--image-background"), taskComplete);
  }

  // Installed before DOMContentLoaded so the gallery loader can safely wait for
  // localized deferred URLs without changing which images it chooses to load.
  window.ShopstaLocalizeGalleryFigure = localizeGalleryFigure;

  function localizeGallery() {
    var root = document.querySelector(".product-gallery") || document;
    Array.prototype.forEach.call(root.querySelectorAll("[data-gallery-figure]:not([data-gallery-index='0'])"), function (figure) {
      localizeGalleryFigure(figure);
    });
    Array.prototype.forEach.call(root.querySelectorAll("[data-gallery-thumbnail]:not([data-gallery-index='0']) .product-gallery--media-thumbnail-img"), function (img) {
      swapGalleryImg(img);
    });
  }

  function start() {
    localizeGallery();
    setTimeout(localizeGallery, 400);
    setTimeout(localizeGallery, 1500);
    if (window.requestIdleCallback) {
      window.requestIdleCallback(localize, { timeout: 2000 });
    } else {
      setTimeout(localize, 200);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
